import { useDispatch, useSelector } from 'react-redux';
import React, { useRef, useState } from 'react'
import { useTranslation } from "react-i18next";
import { LazyLoadImage } from 'react-lazy-load-image-component'
import { useNavigate } from 'react-router-dom';

import { useGalleryContext } from '../../../Contexts/GalleryContext';
import DeleteConfirmation from '../../../SharedComponents/DeleteConfirmation';
import MoveMediaFolder from '../FolderDragDrop/MoveMediaFolder';
import { formatSize } from '../../../../config/global';
import UpdateFolder from '../Folder/UpdateFolder';
import folderIcon from "../../../../assets/img/folder.png"

import EnhancedCheckbox from '../../../SharedComponents/EnhancedCheckbox';
import { Tooltip } from 'primereact/tooltip';
import { Menu } from 'primereact/menu';

import { toggleDeleteDialog, toggleDialog } from '../../../../redux/Slices/Global/globalSlice';
import { useDeleteFolderMutation } from '../../../../queries';
import { deleteItemLocally, setParams } from '../../../../redux/Slices/scrollSlice';
import { formatDateTime } from '../../../../utils/datetime';

function FolderCard({ item, breadCrumb, setBreadCrumb, setCurrentFolder, currentFolder, displayAction = true, onDrop, onDragOver, layoutOverride = null, hideCheckbox = false }) {
    const { t } = useTranslation();
    const { layout: contextLayout, idsList, setIdsList, multiSelectCheck, selectedItem, setSelectedItem } = useGalleryContext();
    // Explicit override takes precedence over the shared (app-wide)
    // GalleryContext layout — used by the campaign builder's compact tile
    // picker, which must NOT flip the real Media Gallery's own layout state.
    const layout = layoutOverride || contextLayout;
    const { openDialog, deleteDialog } = useSelector((state) => state.global);
    const { params, isFetching } = useSelector((state) => state.scroll);

    const [blockedPanel, setBlockedPanel] = useState(false);

    const deleteFolder = useDeleteFolderMutation();
    const dispatch = useDispatch();

    const navigate = useNavigate();
    const menu = useRef(null);

    const items = [
        {
            label: t('table.actions'),
            items: [
                {
                    label: t('buttons.update'),
                    icon: 'pi pi-refresh',
                    command: () => updateHandler()
                },
                {
                    label: t('buttons.delete'),
                    icon: 'pi pi-times',
                    command: () => deleteHandler()
                },
                {
                    label: t('media.media_move'),
                    icon: "pi pi-arrow-right",
                    command: () => popupHandler("moveMediaFolder")
                },
            ]
        },
    ];

    function getSubFolderMedia() {
        if (!isFetching) {
            setBlockedPanel(true)
            setCurrentFolder({
                id: item.id,
                name: item.folder_name
            });
            setBreadCrumb(current => [...current, { label: item.folder_name, id: item.id }]);
            localStorage.setItem("bread_crumb", JSON.stringify([...breadCrumb, { label: item.folder_name, id: item.id }]));
            localStorage.setItem("folder_id", item.id);
            dispatch(setParams({ ...params, folder_id: item.id }))

            if (window.location.pathname.includes("/media/gallery"))
                navigate(`/media/gallery/${item.id}`)

        }
    }

    const onMediaChange = (e) => {
        let selectedMedia = [...idsList];

        if (e.checked)
            selectedMedia.push(e.value);
        else
            selectedMedia.splice(selectedMedia.indexOf(e.value), 1);

        setIdsList(selectedMedia);
    }

    const updateHandler = () => {
        setSelectedItem(item.id)
        dispatch(toggleDialog("updateFolder"))
        setCurrentFolder({
            id: item.id,
            name: item.folder_name
        });
    }

    const popupHandler = (dialogKey) => {
        setSelectedItem(item.id);
        dispatch(toggleDialog(dialogKey))
    }

    const deleteHandler = () => {
        setSelectedItem(item.id)
        dispatch(toggleDeleteDialog("folder"))
    }

    const deleteFolderMutation = async () => {
        await deleteFolder.mutateAsync(item.id,
            {
                onSuccess: () => {
                    dispatch(deleteItemLocally(item.id))
                    dispatch(toggleDeleteDialog("folder"))


                }
            }
        )
    }

    return (
        <>
            {
                layout == "grid" ?
                    <>
                        {/* Grid view: same fixed pp-thumb-grid container as
                            file cards (160px tall × 100% wide), folder icon
                            centered inside via flex. Was a bare LazyLoadImage
                            with `layout_media` that stretched with the column
                            on large monitors. */}
                        <div className="pp-thumb-grid pp-folder-thumb cursor-pointer"
                             onClick={() => !blockedPanel && getSubFolderMedia()}
                             onDrop={event => onDrop(event, item.id)}
                             onDragOver={(event => onDragOver(event, item.id))}>
                            <img src={folderIcon} alt={t('media.media_folder_alt')}
                                 style={{ width: 80, height: 80, objectFit: 'contain' }} />
                        </div>

                        <div className='text-truncate w-100 text-center mt-3  cursor-pointer'
                            onClick={() => !blockedPanel && getSubFolderMedia()}
                            onDrop={event => onDrop(event, item.id)}
                            onDragOver={(event => onDragOver(event, item.id))}
                        >
                            {item.folder_name}
                        </div>
                        <div className='text-truncate w-100 text-center mt-2 fw-light fs-8'>{formatDateTime(item.created_at)}</div>
                        <span className={`position-absolute text-center ${multiSelectCheck ? "folder_action_left" : "folder_action_right"}`} onClick={(event) => menu.current.toggle(event)} >
                            <i className="fas fa-ellipsis-h text-light fs-4" onClick={(event) => menu.current.toggle(event)} aria-controls="popup_menu" aria-haspopup></i>
                            <Menu model={items} popup ref={menu} id="popup_menu" />
                        </span>
                        {
                            multiSelectCheck && !hideCheckbox &&
                            <EnhancedCheckbox
                              inputId={item.id}
                              name="media"
                              className='grid_media_checkbox'
                              value={item.id}
                              onChange={onMediaChange}
                              checked={idsList.indexOf(`${item.id}`) !== -1}
                            />
                        }
                    </>
                    :
                    <>
                        {
                            !displayAction ?
                                <div
                                    className="w-100  "
                                    role="button"
                                >
                                    <div
                                        className="d-grid align-items-center gap-2"
                                        style={{ gridTemplateColumns: "65px 1fr auto" }} // slightly wider icon column
                                    >
                                        {/* List view (no actions) — fixed
                                            64×48 thumbnail container, folder
                                            icon centered inside. */}
                                        <div
                                            className="pp-thumb-list pp-folder-thumb cursor-pointer"
                                            onClick={getSubFolderMedia}
                                            onDrop={onDrop}
                                            onDragOver={onDragOver}
                                        >
                                            <img
                                                src={folderIcon}
                                                alt={t('media.media_folder_alt')}
                                                style={{ width: 38, height: 38, objectFit: 'contain' }}
                                            />
                                        </div>

                                        {/* Folder name */}
                                        <div
                                            className="text-truncate min-w-0 cursor-pointer"
                                            onClick={getSubFolderMedia}
                                            title={item.folder_name}
                                            style={{ fontSize: "0.95rem" }}
                                        >
                                            {item.folder_name}
                                        </div>

                                        {/* Folder content count — was "0 bytes"
                                            because folders don't carry a size.
                                            Show file count instead. */}
                                        <div className="text-nowrap text-end d-none d-md-block text-muted" style={{ fontSize: "0.8rem" }}>
                                            {item.media_count > 0 || item.total_size > 0
                                                ? formatSize(item.total_size)
                                                : t('media.media_files_count', { count: item.media_count ?? 0 })}
                                        </div>
                                    </div>
                                </div>


                                :
                                <>
                                    {/* List view (with actions) — same fixed
                                        64×48 thumbnail container as file
                                        rows. Replaces col-2/col-sm-1 + the
                                        layout_media class that scaled with
                                        the parent column on large screens. */}
                                    <div className="pp-thumb-list pp-folder-thumb me-2" style={{ flexShrink: 0 }}>
                                        <img src={folderIcon} alt={t('media.media_folder_alt')}
                                             onClick={getSubFolderMedia}
                                             style={{ width: 38, height: 38, objectFit: 'contain', cursor: 'pointer' }} />
                                    </div>
                                    <div className="col-3 col-sm-3 col-md-4 text-truncate pe-md-4 text-truncate">{item.folder_name} </div>
                                    <div className="col-2 d-none d-sm-block"></div>
                                    <div className="col-3  col-sm-2 col-md-2 fs-6 text-truncate text-center">
                                        {item.total_size > 0
                                            ? formatSize(item.total_size)
                                            : <span className="text-muted">{t('media.media_files_count', { count: item.media_count ?? 0 })}</span>}
                                    </div>
                                    <div className="col-2 fs-6 text-truncate text-center d-none d-sm-block">
                                        {formatDateTime(item.created_at)}
                                    </div>
                                </>
                        }

                        {
                            displayAction && (
                                <div className="col-3 col-sm-1 text-end d-flex align-items-center justify-content-end">
                                    <div className='actions list_folder_action  '  >
                                        <Tooltip target={`.update-button-${item.id}`} className="fs-8" />
                                        <button className={`btn btn-sm btn-icon update-button-${item.id}`}
                                            data-pr-position="bottom"
                                            data-pr-tooltip={t('buttons.update')}
                                            onClick={() => updateHandler()}>
                                            <i className="pi pi-pencil text-light"></i>
                                        </button>

                                        <Tooltip target={`.delete-button-${item.id}`} className="fs-8" />
                                        <button className={`btn btn-sm btn-icon delete-button-${item.id}`}
                                            data-pr-position="bottom"
                                            data-pr-tooltip={t('buttons.delete')}
                                            onClick={() => deleteHandler(item.id)} >
                                            <i className="bx bx-trash text-light"></i>
                                        </button>

                                        <Tooltip target={`.move-button-${item.id}`} className="fs-8" />
                                        <button
                                            className={`btn btn-sm btn-icon move-button-${item.id}`}
                                            data-pr-position="bottom"
                                            data-pr-tooltip={t('media.media_move')}
                                            onClick={() => popupHandler("moveMediaFolder")}
                                        >
                                            <i className="fas fa-arrow-right text-light "></i>
                                        </button>
                                    </div>
                                    {multiSelectCheck &&
                                        <EnhancedCheckbox
                                          inputId={item.id}
                                          name="media"
                                          className='media_checkbox mx-1 mx-sm-3'
                                          value={item.id}
                                          onChange={onMediaChange}
                                          checked={idsList.indexOf(`${item.id}`) !== -1}
                                        />
                                    }
                                </div>
                            )
                        }
                    </>
            }

            {
                (openDialog.updateFolder && selectedItem == item.id) && <UpdateFolder item={item} currentFolder={currentFolder} />
            }

            {
                (deleteDialog.folder && selectedItem == item.id) &&
                <DeleteConfirmation
                    isLoading={deleteFolder.isLoading}
                    deleteHandler={deleteFolderMutation}
                    itemName={item.folder_name}
                    dialogKey="folder" />
            }

            {(openDialog.moveMediaFolder && selectedItem == item.id) ? <MoveMediaFolder data={item} /> : <></>}
        </>
    )
}

export default FolderCard